import { Component, OnDestroy, OnInit } from '@angular/core'
import {
    Prospect,
    ProspectContact,
    ProspectInteraction,
    ProspectInteractionType,
    ProspectInteractionTypeLabels,
    ProspectInteractionTypeIcons,
} from '@parkupp/core'
import { Subscription } from 'rxjs'
import { ProspectingService } from 'src/app/services/prospecting.service'
import { AuthenticationService } from 'src/app/services/authentication.service'
import { ContactModalService } from './contact-modal.service'

@Component({
    selector: 'app-contact-modal',
    templateUrl: './contact-modal.component.html',
    styleUrls: ['./contact-modal.component.scss'],
})
export class ContactModalComponent implements OnInit, OnDestroy {
    contactKey = ''
    contact: ProspectContact | null = null
    colleagues: ProspectContact[] = []

    busy = false
    error = ''
    avatarFailed = false

    // Intro composer
    composerOpen = false
    draftSubject = ''
    draftBody = ''
    copied = ''
    sending = false
    hasSmtp = false
    selectedTemplateIdx = -1

    // Prospect context (for templates)
    private prospect: Prospect | null = null
    private prospectSub: Subscription | null = null

    templates = [
        {
            name: 'Main Template',
            subject: 'Monthly Parking Near Your Office',
            body: `Good afternoon [name]\nI came across your company whilst looking at businesses near our parking facilities, and wanted to reach out.\nI'm Mark Bradford from ParkUpp - South Africa's monthly parking marketplace (www.parkupp.co.za). I'm reaching out simply to introduce myself and let you know we have parking available at [parking name], which is [distance] from your office. You can see the listing by using this QR code:\nIf your company or anyone on your team is looking for parking, I would really appreciate your forwarding this on. Alternatively please reach out to me directly and I'll be happy to assist where I can.\nWith thanks and kind regards,\nMark Bradford\nYou're receiving this because I thought ParkUpp might be useful for you or someone in your network. If you'd prefer not to hear from me again, just reply "no thanks" and I'll remove you from my list.`,
        },
        {
            name: 'LinkedIn Template',
            subject: '',
            body: `Hi [name], I'm Mark Bradford, founder of ParkUpp. We have monthly parking available in close proximity to your offices. If you know of anybody looking for additional parking, please check availability at www.parkupp.co.za, or feel free to reach out to me directly. Thank you.`,
        },
    ]

    // Edit mode
    editing = false
    editData: Partial<ProspectContact> = {}

    // Hamburger menu
    menuOpen = false

    // Timeline
    interactions: ProspectInteraction[] = []
    interactionTypeLabels = ProspectInteractionTypeLabels
    interactionTypeIcons = ProspectInteractionTypeIcons
    interactionTypes = Object.values(ProspectInteractionType).filter(
        (t) => t !== ProspectInteractionType.STATUS_CHANGE
    )
    newInteraction = { type: ProspectInteractionType.NOTE as ProspectInteractionType, summary: '' }
    addingInteraction = false
    activeTab: 'profile' | 'timeline' = 'profile'

    private openSub: Subscription | null = null
    private dataSub: Subscription | null = null
    private colleaguesSub: Subscription | null = null
    private interactionsSub: Subscription | null = null
    private smtpSub: Subscription | null = null
    private colleaguesKey = ''

    constructor(
        private prospecting: ProspectingService,
        private authService: AuthenticationService,
        public modal: ContactModalService
    ) {}

    ngOnInit(): void {
        this.openSub = this.modal.openKey$.subscribe((key) => {
            if (key) {
                this.load(key)
            } else {
                this.reset()
            }
        })
    }

    ngOnDestroy(): void {
        this.openSub?.unsubscribe()
        this.dataSub?.unsubscribe()
        this.prospectSub?.unsubscribe()
        this.colleaguesSub?.unsubscribe()
        this.interactionsSub?.unsubscribe()
        this.smtpSub?.unsubscribe()
    }

    private load(key: string): void {
        this.dataSub?.unsubscribe()
        this.interactionsSub?.unsubscribe()
        this.smtpSub?.unsubscribe()
        this.prospectSub?.unsubscribe()
        this.contactKey = key
        this.error = ''
        this.editing = false
        this.activeTab = 'profile'
        this.avatarFailed = false
        this.closeComposer()
        this.dataSub = this.prospecting.getContact(key).subscribe((c) => {
            this.contact = c
            if (c?.prospectKey) {
                this.loadColleagues(c.prospectKey)
                this.loadProspect(c.prospectKey)
            }
        })
        this.interactionsSub = this.prospecting.listInteractionsForContact(key).subscribe((i) => (this.interactions = i))
        const uid = this.authService.user?.uid
        if (uid) {
            this.smtpSub = this.prospecting.getSmtpSettings(uid).subscribe((s) => {
                this.hasSmtp = !!(s && s.host && s.user)
            })
        }
    }

    private loadProspect(prospectKey: string): void {
        this.prospectSub?.unsubscribe()
        this.prospectSub = this.prospecting.getProspect(prospectKey).subscribe((p) => (this.prospect = p))
    }

    private loadColleagues(prospectKey: string): void {
        if (prospectKey === this.colleaguesKey) return
        this.colleaguesKey = prospectKey
        this.colleaguesSub?.unsubscribe()
        this.colleaguesSub = this.prospecting.listContactsForProspect(prospectKey).subscribe((list) => {
            this.colleagues = list.filter((c) => c.$key !== this.contactKey)
        })
    }

    private reset(): void {
        this.dataSub?.unsubscribe()
        this.dataSub = null
        this.prospectSub?.unsubscribe()
        this.prospectSub = null
        this.prospect = null
        this.colleaguesSub?.unsubscribe()
        this.colleaguesSub = null
        this.colleaguesKey = ''
        this.colleagues = []
        this.contactKey = ''
        this.contact = null
        this.closeComposer()
    }

    openColleague(c: ProspectContact): void {
        if (c.$key) this.modal.open(c.$key)
    }

    colleagueName(c: ProspectContact): string {
        return c.fullName || [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email
    }

    colleagueAvatar(c: ProspectContact): string {
        return c.linkedInProfilePicStored || c.linkedInProfilePic || ''
    }

    colleagueInitials(c: ProspectContact): string {
        const name = this.colleagueName(c)
        const parts = name.split(/[\s@.]+/).filter(Boolean)
        return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase()
    }

    onAvatarError(): void {
        this.avatarFailed = true
    }

    telLink(): string {
        return 'tel:' + (this.contact?.phone || '').replace(/[^\d+]/g, '')
    }

    whatsAppLink(): string {
        let digits = (this.contact?.phone || '').replace(/[^\d]/g, '')
        // South-Africa-centric normalisation: a leading 0 becomes the 27 country code.
        if (digits.startsWith('0')) {
            digits = '27' + digits.slice(1)
        }
        return 'https://wa.me/' + digits
    }

    close(): void {
        this.modal.close()
    }

    displayName(): string {
        const c = this.contact
        if (!c) return ''
        return c.fullName || [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email
    }

    initials(): string {
        const name = this.displayName()
        const parts = name.split(/[\s@.]+/).filter(Boolean)
        return (parts[0]?.[0] || '') + (parts[1]?.[0] || '')
    }

    bannerStyle(): { [key: string]: string } {
        const c = this.contact
        const url = c?.linkedInBackgroundPicStored || c?.linkedInBackgroundPic
        if (url) {
            return { 'background-image': `url(${url})` }
        }
        return {}
    }

    avatarUrl(): string {
        const c = this.contact
        return c?.linkedInProfilePicStored || c?.linkedInProfilePic || ''
    }

    async verifyEmail(): Promise<void> {
        if (!this.contact?.$key) return
        this.busy = true
        this.error = ''
        try {
            await this.prospecting.verifyContactEmail(this.contact.$key)
        } catch (e: any) {
            this.error = e?.message || 'Verify failed'
        } finally {
            this.busy = false
        }
    }

    async findEmail(): Promise<void> {
        if (!this.contact?.$key) return
        this.busy = true
        this.error = ''
        try {
            const res = await this.prospecting.findContactEmail(this.contact.$key)
            if (!res?.email) this.error = 'No email found for this contact'
        } catch (e: any) {
            this.error = e?.message || 'Find email failed'
        } finally {
            this.busy = false
        }
    }

    async scrapeLinkedIn(): Promise<void> {
        if (!this.contact?.$key) return
        const url = this.contact.linkedInUrl || prompt('LinkedIn profile URL for ' + this.displayName() + ':') || ''
        if (!url) return
        this.busy = true
        this.error = ''
        try {
            await this.prospecting.scrapeContactLinkedIn(this.contact.$key, url)
        } catch (e: any) {
            this.error = e?.message || 'LinkedIn scrape failed'
        } finally {
            this.busy = false
        }
    }

    copyEmail(): void {
        if (!this.contact?.email) return
        this.copyText(this.contact.email, 'email')
    }

    // ---------- Intro composer ----------

    openComposer(): void {
        this.composerOpen = true
        this.draftSubject = ''
        this.draftBody = ''
        this.copied = ''
        this.selectedTemplateIdx = -1
        const drafts = this.contact?.emailDrafts
        if (drafts?.length) {
            const last = drafts[drafts.length - 1]
            this.draftSubject = last.subject
            this.draftBody = last.body
        }
    }

    closeComposer(): void {
        this.composerOpen = false
    }

    applyTemplate(idx: number): void {
        if (idx < 0 || idx >= this.templates.length) return
        const tpl = this.templates[idx]
        const name = this.contact?.firstName || this.contact?.fullName || '[name]'
        const nearest = this.prospect?.nearestParkings?.[0]
        const parkingName = nearest?.parkingName || '[parking name]'
        const distance = nearest ? `${nearest.distanceKm} km` : '[distance]'

        this.draftSubject = tpl.subject
            .replace(/\[name\]/gi, name)
            .replace(/\[parking name\]/gi, parkingName)
            .replace(/\[distance\]/gi, distance)

        this.draftBody = tpl.body
            .replace(/\[name\]/gi, name)
            .replace(/\[parking name\]/gi, parkingName)
            .replace(/\[distance\]/gi, distance)
    }

    copyText(text: string, what: string): void {
        navigator.clipboard.writeText(text).then(() => {
            this.copied = what
            setTimeout(() => (this.copied = ''), 1500)
        })
    }

    copyFull(): void {
        this.copyText(`Subject: ${this.draftSubject}\n\n${this.draftBody}`, 'full')
    }

    async sendEmail(): Promise<void> {
        if (!this.contact?.email || !this.draftSubject || !this.draftBody) return
        this.sending = true
        this.error = ''
        try {
            await this.prospecting.sendOutreachEmail({
                contactKey: this.contact.$key,
                prospectKey: this.contact.prospectKey,
                to: this.contact.email,
                subject: this.draftSubject,
                body: this.draftBody,
                denormProspectName: this.contact.denormProspectName,
                denormContactName: this.contact.fullName || this.contact.email,
            })
            this.copied = 'sent'
            setTimeout(() => (this.copied = ''), 2000)
        } catch (e: any) {
            this.error = e?.message || 'Send failed'
        } finally {
            this.sending = false
        }
    }

    // ---------- Edit ----------

    startEdit(): void {
        if (!this.contact) return
        this.editData = {
            fullName: this.contact.fullName,
            firstName: this.contact.firstName,
            lastName: this.contact.lastName,
            email: this.contact.email,
            position: this.contact.position,
            phone: this.contact.phone,
            linkedInUrl: this.contact.linkedInUrl,
        }
        this.editing = true
    }

    cancelEdit(): void {
        this.editing = false
    }

    async saveEdit(): Promise<void> {
        if (!this.contact?.$key) return
        try {
            await this.prospecting.updateContact(this.contact.$key, this.editData)
            this.editing = false
        } catch (e: any) {
            this.error = e?.message || 'Could not save changes'
        }
    }

    // ---------- Timeline ----------

    async logInteraction(): Promise<void> {
        if (!this.newInteraction.summary.trim() || this.addingInteraction) return
        this.addingInteraction = true
        try {
            await this.prospecting.addInteraction({
                prospectKey: this.contact?.prospectKey,
                denormProspectName: this.contact?.denormProspectName,
                contactKey: this.contactKey,
                denormContactName: this.contact?.fullName || this.contact?.email,
                type: this.newInteraction.type,
                summary: this.newInteraction.summary,
            })
            this.newInteraction = { type: ProspectInteractionType.NOTE, summary: '' }
        } catch (e: any) {
            this.error = e?.message || 'Could not log interaction'
        } finally {
            this.addingInteraction = false
        }
    }

    interactionDate(i: ProspectInteraction): Date | null {
        const ts: any = i.createdAt
        if (!ts) return null
        if (ts.toDate) return ts.toDate()
        return new Date(ts)
    }
}
